import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

/**
 * Date and number formatting.
 *
 * Centralised so dates look the same everywhere, and so every function tolerates bad
 * input. An API response with an unexpected null should never crash a page render — a
 * dash in place of a date is a far better outcome than a white screen.
 */

/** `17 August 2026`. Day-first, which is the convention in both the UAE and India. */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'd MMMM yyyy') : '—';
}

/** `17 Aug 2026, 14:30` — for admin tables where the time matters. */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'd MMM yyyy, HH:mm') : '—';
}

/** `3 days ago`. For activity feeds, where precision is less useful than recency. */
export function formatRelative(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? `${formatDistanceToNowStrict(date)} ago` : '—';
}

/** For a `datetime` attribute — always ISO, never localised. */
export function toIsoString(value: string | Date | null | undefined): string | undefined {
  const date = toDate(value);
  return date?.toISOString();
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? parseISO(value) : value;
  return isValid(date) ? date : null;
}

/** `1.4 MB`. Binary units, matching what an operating system reports. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;

  // No decimal on bytes — "512.0 B" reads oddly.
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/** `1,240`. Grouped thousands, for dashboard counters. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

/**
 * Up to two initials from a display name, for an avatar disc.
 *
 * First and *last* initial rather than the first two: "Maria del Carmen Ruiz" reads as MR
 * to anyone who knows her and as MD to nobody. Falls back to "?" so a name that yields no
 * letters still produces a disc with something in it rather than an empty circle.
 */
export function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return `${first}${last}`.toUpperCase() || '?';
}

/** Turn `IN_PROGRESS` into `In progress` for display. */
export function humaniseEnum(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}
